import React from 'react';
import { Box, Button, Typography } from '@mui/material';
import { useTranslation } from 'react-i18next';
import {
  marketingColor,
  marketingFont,
  marketingLayout,
  marketingLine,
  marketingShadow,
} from '../../theme/marketingTokens';
import { useHeroParallax } from './useHeroParallax';
import {
  Eyebrow,
  MarketingImageSlot,
  ctaButtonSx,
  ghostButtonSx,
  marketingImages,
} from './MarketingPrimitives';

/** Floating chip shared shell — hairline card, drop shadow, float animation. */
const chipSx = (animation: string, transform: string, transition: string) =>
  ({
    position: 'absolute',
    backgroundColor: marketingColor.surface,
    border: `1px solid ${marketingLine.hairlineStrong}`,
    borderRadius: '6px',
    boxShadow: marketingShadow.chip,
    fontFamily: marketingFont.mono,
    transform,
    transition,
    animation,
    zIndex: 2,
  } as const);

interface HeroSectionProps {
  onPrimary: () => void;
  onSecondary: () => void;
}

export const HeroSection: React.FC<HeroSectionProps> = ({ onPrimary, onSecondary }) => {
  const { t } = useTranslation() as { t: (key: string) => string };
  const parallax = useHeroParallax();

  return (
    <Box
      component="section"
      sx={{
        maxWidth: marketingLayout.maxWidth,
        mx: 'auto',
        px: marketingLayout.sectionPaddingX,
        pt: { xs: 7, md: 12.5 },
        pb: { xs: 7, md: 11 },
        display: 'flex',
        alignItems: 'center',
        gap: { xs: 5, md: 8 },
        flexWrap: 'wrap',
      }}
    >
      <Box sx={{ flex: 1, minWidth: { xs: '100%', sm: 340 } }}>
        <Eyebrow sx={{ mb: 3.5 }}>{t('public.home.hero.eyebrow')}</Eyebrow>
        <Typography
          variant="h1"
          sx={{ mb: 3, fontSize: { xs: '2.5rem', md: '3.625rem' } }}
        >
          {t('public.home.hero.titleLead')}{' '}
          <Box component="span" sx={{ color: marketingColor.accent }}>
            {t('public.home.hero.titleAccent')}
          </Box>
        </Typography>
        <Typography
          sx={{
            fontSize: 16.5,
            lineHeight: 1.7,
            color: marketingColor.textSecondary,
            maxWidth: 470,
            mb: 4.25,
          }}
        >
          {t('public.home.hero.description')}
        </Typography>
        <Box sx={{ display: 'flex', gap: 1.75, flexWrap: 'wrap', mb: 2.5 }}>
          <Button disableElevation sx={ctaButtonSx} onClick={onPrimary}>
            {t('public.home.hero.ctaPrimary')}
          </Button>
          <Button sx={ghostButtonSx} onClick={onSecondary}>
            {t('public.home.hero.ctaSecondary')}
          </Button>
        </Box>
        <Typography
          sx={{ fontFamily: marketingFont.mono, fontSize: 12, color: marketingColor.textMuted }}
        >
          {t('public.home.hero.note')}
        </Typography>
      </Box>

      <Box
        onMouseMove={parallax.onMouseMove}
        onMouseLeave={parallax.onMouseLeave}
        sx={{
          flex: 1,
          minWidth: { xs: '100%', sm: 340 },
          position: 'relative',
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          perspective: '1200px',
          minHeight: { xs: 320, md: 480 },
        }}
      >
        {/* Accent glow, deepest layer */}
        <Box
          aria-hidden
          sx={{
            position: 'absolute',
            width: 420,
            height: 420,
            borderRadius: '999px',
            background:
              'radial-gradient(circle, rgba(95,226,138,0.14) 0%, transparent 65%)',
            transform: parallax.layer(40),
            transition: parallax.transition,
            pointerEvents: 'none',
          }}
        />

        <MarketingImageSlot
          src={marketingImages.heroDashboard}
          alt={t('public.home.hero.dashboardAlt')}
          placeholder={t('public.home.hero.dashboardPlaceholder')}
          sx={{
            width: '100%',
            maxWidth: 520,
            height: { xs: 260, md: 370 },
            border: `1px solid ${marketingLine.hairlineStrong}`,
            borderRadius: '4px',
            animation: 'motzuPulseGlow 5s ease-in-out infinite',
            transform: `rotateY(${parallax.x * 4}deg) rotateX(${-parallax.y * 4}deg)`,
            transition: parallax.transition,
          }}
        />

        {/* 1 — ingest JSON */}
        <Box
          sx={{
            ...chipSx(
              'motzuFloatY 6s ease-in-out infinite',
              parallax.layer(-22),
              parallax.transition
            ),
            top: '2%',
            left: { xs: 0, md: '-2%' },
            px: 2,
            py: 1.5,
            fontSize: 11.5,
            lineHeight: 1.7,
            color: marketingColor.textSecondary,
            display: { xs: 'none', sm: 'block' },
          }}
        >
          <Box sx={{ color: marketingColor.accent }}>{'// ingest'}</Box>
          <Box>"model": "Alpha 7 IV"</Box>
          <Box>
            "price": <Box component="span" sx={{ color: marketingColor.accent }}>2199.99</Box>
          </Box>
        </Box>

        {/* 2 — MAP compliance */}
        <Box
          sx={{
            ...chipSx(
              'motzuFloatY 7s ease-in-out 0.8s infinite',
              parallax.layer(16),
              parallax.transition
            ),
            bottom: '6%',
            left: '4%',
            px: 1.75,
            py: 1.25,
            display: { xs: 'none', sm: 'flex' },
            alignItems: 'center',
            gap: 1,
          }}
        >
          <Box
            sx={{
              width: 7,
              height: 7,
              borderRadius: '999px',
              backgroundColor: marketingColor.accent,
              boxShadow: `0 0 8px ${marketingColor.accent}`,
              animation: 'motzuBlink 2s ease-in-out infinite',
            }}
          />
          <Box sx={{ fontSize: 11.5, color: marketingColor.text }}>MAP compliant</Box>
        </Box>

        {/* 3 — Shopify sync */}
        <Box
          sx={{
            ...chipSx(
              'motzuFloatY 5.5s ease-in-out 0.4s infinite',
              parallax.layer(-30),
              parallax.transition
            ),
            top: '14%',
            right: { xs: 0, md: '-2%' },
            px: 1.75,
            py: 1.25,
            display: { xs: 'none', sm: 'block' },
          }}
        >
          <Box
            sx={{
              fontSize: 10.5,
              color: marketingColor.textMuted,
              textTransform: 'uppercase',
              mb: 0.4,
            }}
          >
            Shopify
          </Box>
          <Box sx={{ fontSize: 11.5, color: marketingColor.accent }}>✓ synced · 1,204 SKUs</Box>
        </Box>

        {/* 4 — rebate detected */}
        <Box
          sx={{
            ...chipSx(
              'motzuFloatY 6.5s ease-in-out 1.2s infinite',
              parallax.layer(24),
              parallax.transition
            ),
            bottom: '14%',
            right: '2%',
            px: 1.75,
            py: 1.25,
            display: { xs: 'none', sm: 'block' },
          }}
        >
          <Box
            sx={{
              fontSize: 10.5,
              color: marketingColor.textMuted,
              textTransform: 'uppercase',
              mb: 0.4,
            }}
          >
            Rebate detected
          </Box>
          <Box sx={{ fontSize: 11.5, color: marketingColor.text }}>
            -$500.00 <Box component="span" sx={{ color: marketingColor.accent }}>applied</Box>
          </Box>
        </Box>
      </Box>
    </Box>
  );
};
